import { useState, useCallback, useEffect } from "react";
import { invoke } from "../lib/bridge";
import type { ToastData } from "../components/Toast";

interface PendingJoinRequest {
  id: number;
  memberName: string;
  publicKey: string;
  deviceLink: boolean;
  createdAt: string;
}

interface UseJoinRequestToastsOptions {
  isVisible: boolean;
}

interface UseJoinRequestToastsResult {
  toasts: ToastData[];
  setToasts: React.Dispatch<React.SetStateAction<ToastData[]>>;
  removeToast: (id: string) => void;
}

/**
 * Polls for pending join requests every 10 s (while the app is visible) and
 * manages the corresponding "X wants to join" permanent toast actions.
 */
export function useJoinRequestToasts({ isVisible }: UseJoinRequestToastsOptions): UseJoinRequestToastsResult {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [dismissedRequests, setDismissedRequests] = useState<Set<number>>(new Set());

  const removeToast = useCallback((id: string) => {
    setToasts(prev => {
      const toast = prev.find(t => t.id === id);
      if (toast?.joinRequestId != null) {
        setDismissedRequests(d => new Set(d).add(toast.joinRequestId as number));
      }
      return prev.filter(t => t.id !== id);
    });
  }, []);

  useEffect(() => {
    const poll = async () => {
      try {
        const requests = await invoke<PendingJoinRequest[]>("list_join_requests");
        setToasts(prev => {
          const existing = new Set(prev.map(t => t.id));
          const newToasts = [...prev];

          for (const req of requests) {
            // Device-link requests are your own other devices and are auto-handled
            // by the Link a device screen — never shown as "X wants to join".
            if (req.deviceLink) continue;
            const toastId = `join-${req.id}`;
            if (!existing.has(toastId) && !dismissedRequests.has(req.id)) {
              newToasts.push({
                id: toastId,
                joinRequestId: req.id,
                message: `${req.memberName} wants to join your family`,
                type: "permanent",
                actions: [
                  {
                    label: "Accept",
                    variant: "primary",
                    onClick: async () => {
                      try {
                        await invoke("approve_join_request", { requestId: req.id, joinerPublicKeyB64: req.publicKey });
                      } catch (e) {
                        console.error("Failed to approve:", e);
                      }
                      setToasts(p => p.filter(t => t.id !== toastId));
                    },
                  },
                  {
                    label: "Reject",
                    variant: "danger",
                    onClick: async () => {
                      try {
                        await invoke("reject_join_request", { requestId: req.id });
                      } catch (e) {
                        console.error("Failed to reject:", e);
                      }
                      setToasts(p => p.filter(t => t.id !== toastId));
                    },
                  },
                ],
              });
            }
          }

          // Remove toasts for requests that are no longer pending (identified by
          // the structured joinRequestId field, not by parsing the id string).
          const pendingReqIds = new Set(requests.map(r => r.id));
          return newToasts.filter(t => t.joinRequestId == null || pendingReqIds.has(t.joinRequestId));
        });
      } catch {
        // Not authenticated or offline
      }
    };

    poll();
    if (!isVisible) return;
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, [dismissedRequests, isVisible]);

  return { toasts, setToasts, removeToast };
}
